import { Box, Button, CircularProgress, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import dayjs from 'dayjs';
import { useQuery } from "@tanstack/react-query";
import { orderService, Order, OrderItem } from '../services/orderService';

type MyOrderProps = {
  children?: React.ReactNode;
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'completed':
    case 'Delivered':
      return { color: '#2e7d32', icon: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} /> };
    case 'failed':
    case 'Unpaid':
      return { color: '#d32f2f', icon: <ErrorOutlineOutlinedIcon sx={{ fontSize: 18 }} /> };
    case 'pending':
    case 'Order placed':
      return { color: '#000', icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} /> };
    case 'on-hold':
    case 'processing':
    case 'shipping':
      return { color: '#2e7d32', icon: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} /> };
    default:
      return { color: '#666', icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} /> };
  }
};

const OrderGroup = ({ order, onViewDetail }: { order: Order; onViewDetail: () => void }) => {
  const { color, icon } = getStatusConfig(order.status);
  const isUnpaid = order.status === 'Unpaid';
  const firstItem: OrderItem | undefined = order.items[0];
  const totalPrice = order.items.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0);

  return (
    <Paper elevation={0} sx={{ borderBottom: '1px solid #eee', mb: 4, pb: 2, borderRadius: 0 }}>

      {/* --- Section 1: Header Status Bar --- */}
      <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          Ordered on {dayjs(order.date_created ?? order.date).format('DD MMM YYYY')}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ display: 'flex', color: color }}>{icon}</Box>
          <Typography variant="subtitle2" sx={{ color: color, fontWeight: 700 }}>
            {order.status}
          </Typography>

          {isUnpaid && (
            <Button
              variant="text"
              size="small"
              sx={{ color: 'black', textDecoration: 'underline', fontWeight: 800, ml: 1, p: 0, minWidth: 0, textTransform: 'none' }}
            >
              Go to Payment
            </Button>
          )}

          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#ccc', ml: 0.5 }} />
        </Stack>
      </Box>

      {/* --- Section 2: Table Header --- */}
      <Grid container sx={{ px: 2, py: 1, borderBottom: '1px solid #eee' }}>
        <Grid size={{ xs: 6 }}>
          <Typography variant="caption" color="text.secondary">Order detail</Typography>
        </Grid>
        <Grid size={{ xs: 2 }} sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Size</Typography>
        </Grid>
        <Grid size={{ xs: 2 }} sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Quantity</Typography>
        </Grid>
        <Grid size={{ xs: 2 }} sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">Price</Typography>
        </Grid>
      </Grid>

      {/* --- Section 3: First Product Row --- */}
      {firstItem && (
        <Grid container sx={{ px: 2, py: 3, alignItems: 'center' }}>
          <Grid size={{ xs: 6 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Box
                component="img"
                src={firstItem.image}
                sx={{ width: 70, height: 90, objectFit: 'cover', bgcolor: '#f9f9f9' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {firstItem.name}
              </Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 2 }} sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              {firstItem.attributes?.find(a => a.name === 'Size')?.value ?? '-'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 2 }} sx={{ textAlign: 'center' }}>
            <Typography variant="body2">x {firstItem.quantity}</Typography>
          </Grid>
          <Grid size={{ xs: 2 }} sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ฿{Number(firstItem.price).toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      )}

      {/* --- Section 4: View Details Button --- */}
      <Box sx={{ textAlign: 'center' }}>
        <Button onClick={() => { onViewDetail(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} endIcon={<ChevronRightIcon />} sx={{ color: '#666', textTransform: 'none' }}>
          View details
        </Button>
      </Box>

      <Divider sx={{ borderStyle: 'dashed', mx: 2, mb: 2 }} />

      {/* --- Section 5: Total Summary --- */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 5, px: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total
          <Box component="span" sx={{ color: 'black', fontWeight: 700, ml: 1 }}>
            {order.items.length} Items
          </Box>
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          ฿{totalPrice.toLocaleString()}
        </Typography>
      </Box>
    </Paper>
  );
};

const OrderDetailView = ({ orderId, onBack }: { orderId: string; onBack: () => void }) => {
  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => orderService.getOrderById(orderId),
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#1a1a1a' }} /></Box>;
  if (isError || !order) return <Typography color="error">Failed to load order detail.</Typography>;

  const items = order.items ?? [];
  const totalPrice = items.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0);

  const STATUS_STEP: Record<string, number> = {
    pending: 1,
    processing: 2,
    shipping: 3,
    completed: 4,
  };
  const completedSteps = STATUS_STEP[order.status] ?? 1;
  const orderDate = dayjs(order.date_created ?? order.date).format('DD MMM YYYY  HH:mm');
  const timeline = [
    { label: 'Order placed', date: orderDate },
    { label: 'Paid',         date: completedSteps >= 2 ? orderDate : '' },
    { label: 'Shipping',     date: completedSteps >= 3 ? orderDate : '' },
    { label: 'Delivered',    date: completedSteps >= 4 ? orderDate : '' },
  ];

  const getStepIcon = (label: string) => {
    switch (label) {
      case 'Order placed': return <ReceiptLongOutlinedIcon sx={{ fontSize: 24 }} />;
      case 'Paid': return <CreditCardIcon sx={{ fontSize: 24 }} />;
      case 'Shipping': return <LocalShippingOutlinedIcon sx={{ fontSize: 24 }} />;
      case 'Delivered': return <LocalShippingOutlinedIcon sx={{ fontSize: 24 }} />;
      default: return <InfoOutlinedIcon sx={{ fontSize: 24 }} />;
    }
  };

  return (
    <Box>
      {/* --- Header & Back Button --- */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ cursor: 'pointer' }}
          onClick={onBack}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px' }}>
          Order No. #{String(order.order_id).padStart(7, '0')}
        </Typography>
      </Stack>
      {/* --- Section: Stepper Status --- */}
      <Box sx={{ mb: 6, px: { xs: 0, md: 2 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          {timeline.map((step, idx) => {
            const isCompleted = step.date !== '';
            return (
              <React.Fragment key={idx}>
                <Stack alignItems="center" spacing={1} sx={{ flex: 1, position: 'relative' }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ color: isCompleted ? 'black' : '#ccc', display: 'flex' }}>
                      {getStepIcon(step.label)}
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: isCompleted ? 'black' : '#ccc' }}>
                      {step.label}
                    </Typography>
                    {isCompleted && (
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                    )}
                  </Stack>
                  {step.label === 'Shipping' && isCompleted && order.tracking?.tracking_number && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <Box
                        component="img"
                        src="https://www.dhl.com/content/dam/dhl/global/core/images/logos/dhl-logo.svg"
                        alt="DHL"
                        sx={{ height: 14, objectFit: 'contain' }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #D0D0D0', borderRadius: '8px', px: 1, py: 0.5 }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: 0.5 }}>
                          {order.tracking.tracking_number}
                        </Typography>
                      </Box>
                      <Box
                        onClick={() => navigator.clipboard.writeText(order.tracking!.tracking_number)}
                        sx={{ cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center' }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Stack>
                  )}
                </Stack>

                {idx < timeline.length - 1 && (
                  <Box sx={{ flex: 1, height: '1px', borderBottom: '2px dashed #eee', mt: '12px', mx: -1 }} />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      </Box>

      {/* --- Section: Shipping Address --- */}
      {order.shipping && (
        <Box sx={{ mb: 4, width: { xs: '100%', md: '480px' } }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#333', mb: 1 }}>
            Shipping Address
          </Typography>
          <Box sx={{ border: '1px solid #D0D0D0', borderRadius: '10px', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 0.75, borderBottom: '1px solid #E0E0E0' }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                {order.shipping.first_name} {order.shipping.last_name}
              </Typography>
            </Box>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#333', pb: 1.5, mt: '1em' }}>
                {order.shipping.first_name} {order.shipping.last_name}
                {'   '}
                <span style={{ fontWeight: 400, color: '#555' }}>{order.billing?.phone}</span>
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#666', mt: '1em' }}>
                {order.shipping.country}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#666', mb: '1em' }}>
                {order.shipping.address_1}
                {order.shipping.address_2 ? `, ${order.shipping.address_2}` : ''}
                {`, ${order.shipping.city} ${order.shipping.postcode}`}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* --- Section: Order Items Table --- */}
      <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          Ordered on {dayjs(order.date_created ?? order.date).format('DD MMM YYYY')}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LocalShippingOutlinedIcon sx={{ fontSize: 18, color: order.status === 'Unpaid' ? '#d32f2f' : '#2e7d32' }} />
          <Typography variant="caption" sx={{ color: order.status === 'Unpaid' ? '#d32f2f' : '#2e7d32', fontWeight: 800 }}>
            {order.status}
          </Typography>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#ccc' }} />
        </Stack>
      </Box>

      <Grid container sx={{ px: 2, py: 1, borderBottom: '1px solid #eee' }}>
        <Grid size={{ xs: 6 }}><Typography variant="caption" color="text.secondary">Order detail</Typography></Grid>
        <Grid size={{ xs: 2 }} sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Size</Typography></Grid>
        <Grid size={{ xs: 2 }} sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Quantity</Typography></Grid>
        <Grid size={{ xs: 2 }} sx={{ textAlign: 'right' }}><Typography variant="caption" color="text.secondary">Price</Typography></Grid>
      </Grid>

      {items.map((item, idx) => (
        <Grid container key={idx} sx={{ py: 3, px: 2, borderBottom: '1px solid #eee', alignItems: 'center' }}>
          <Grid size={{ xs: 6 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Box component="img" src={item.image} sx={{ width: 65, height: 85, objectFit: 'cover', bgcolor: '#f9f9f9' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 2 }} sx={{ textAlign: 'center' }}>
            {item.attributes?.find(a => a.name === 'Size')?.value ?? '-'}
          </Grid>
          <Grid size={{ xs: 2 }} sx={{ textAlign: 'center' }}>x {item.quantity}</Grid>
          <Grid size={{ xs: 2 }} sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>฿{Number(item.price).toLocaleString()}</Typography>
          </Grid>
        </Grid>
      ))}

      {/* --- Section: Payment Summary --- */}
      <Box sx={{ mt: 4, px: 2 }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Tax</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>฿ 0</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Shipping fee</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Free</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Total <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 0.5 }}>(Baht)</Box>
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{totalPrice.toLocaleString()}</Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

const MyOrder: React.FC<MyOrderProps> = () => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedOrderId) {
      window.history.pushState(null, '', window.location.pathname);
      const handlePopState = () => { setSelectedOrderId(null); };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [selectedOrderId]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['orders'],
    queryFn: orderService.getOrders,
  });

  const orders: Order[] = data?.orders ?? [];

  return (
    <Box sx={{ mx: 'auto', mt: 4, mr: { xs: 0, md: 50 }, width: '100%' }}>
      <Typography variant="h3" align="center" sx={{ mb: 6, fontFamily: 'Nandia' }}>
        My Account
      </Typography>

      {selectedOrderId === null ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>My Order</Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#1a1a1a' }} />
            </Box>
          ) : isError ? (
            <Typography color="error">Failed to load orders. Please try again.</Typography>
          ) : orders.length === 0 ? (
            <Stack alignItems="center" spacing={4} sx={{ py: 10 }}>
              <Typography variant="h5" sx={{ fontFamily: 'Nandia', color: 'text.secondary', fontWeight: 400 }}>
                You haven't placed any orders yet.
              </Typography>
              <Button
                variant="contained"
                sx={{ bgcolor: '#1a1a1a', color: 'white', borderRadius: 2, px: 6, py: 1.5, textTransform: 'none', fontSize: '1rem', '&:hover': { bgcolor: '#333' } }}
                onClick={() => window.location.href = '/all-products'}
              >
                Explore Collection
              </Button>
            </Stack>
          ) : (
            <Stack spacing={4}>
              {orders.map((order) => (
                <OrderGroup
                  key={order.order_id}
                  order={order}
                  onViewDetail={() => setSelectedOrderId(String(order.order_id))}
                />
              ))}
            </Stack>
          )}
        </>
      ) : (
        <OrderDetailView orderId={selectedOrderId!} onBack={() => setSelectedOrderId(null)} />
      )}
    </Box>
  );
};

export default MyOrder;
